/**
 * CourseAnnouncementsBoard — the "tablón" below the Kurs tiles on Home.
 *
 * Students see the list read-only. Teachers (admin + teacher) also get a tiny
 * composer ("Neue Mitteilung …") and a trash icon next to every entry so they
 * can take down outdated notices. Auto-announcements (emitted when a teacher
 * uploads a material) are visually marked with a small badge so the teacher
 * doesn't confuse them with hand-written posts.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Megaphone, PaperPlaneTilt, Trash, Sparkle, Spinner,
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LangContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso, isDe) {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return isDe ? 'gerade eben' : 'ahora mismo';
  if (m < 60) return isDe ? `vor ${m} Min.` : `hace ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return isDe ? `vor ${h} Std.` : `hace ${h} h`;
  const days = Math.floor(h / 24);
  if (days < 7) return isDe ? `vor ${days} T.` : `hace ${days} d`;
  return d.toLocaleDateString(isDe ? 'de-DE' : 'es-ES');
}

export default function CourseAnnouncementsBoard() {
  const { user } = useAuth();
  const { lang } = useLang();
  const isDe = lang !== 'es';
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/announcements`, { withCredentials: true });
      setItems(res.data || []);
    } catch { /* silent — board is non-critical */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const publish = async () => {
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    try {
      await axios.post(`${API}/announcements`, { body: text }, { withCredentials: true });
      setDraft('');
      load();
      toast.success(isDe ? 'Mitteilung veröffentlicht' : 'Anuncio publicado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally { setPosting(false); }
  };

  const remove = async (announcement_id) => {
    if (!window.confirm(isDe ? 'Diese Mitteilung löschen?' : '¿Borrar este aviso?')) return;
    try {
      await axios.delete(`${API}/announcements/${announcement_id}`, { withCredentials: true });
      setItems(prev => prev.filter(a => a.announcement_id !== announcement_id));
    } catch { toast.error('Error'); }
  };

  return (
    <section className="mt-5" data-testid="kurs-announcement-board">
      <div className="bg-white rounded-2xl border border-stone-200 p-4 sm:p-5 space-y-3">
        <h3 className="font-heading text-base sm:text-lg font-bold text-[#292524] flex items-center gap-2">
          <Megaphone size={20} weight="duotone" className="text-[#AB1C24]" />
          {isDe ? 'Mitteilungen des Lehrers' : 'Avisos del profesor'}
        </h3>

        {isTeacher && (
          <div className="space-y-2 bg-[#FAF9F6] rounded-lg border border-stone-200 p-3">
            <textarea
              value={draft}
              onChange={e => setDraft(e.target.value)}
              rows={2}
              maxLength={600}
              placeholder={isDe
                ? 'z.B. Am 23.5. fällt der Kurs wegen Feiertag aus.'
                : 'p.ej. El 23/5 no habrá clase por festivo.'}
              className="w-full bg-white border border-stone-300 rounded px-2 py-1.5 text-sm"
              data-testid="announcement-draft"
            />
            <div className="flex items-center justify-between">
              <span className="text-[11px] text-[#A8A29E]">{draft.length}/600</span>
              <button
                onClick={publish}
                disabled={posting || !draft.trim()}
                className="inline-flex items-center gap-1 bg-[#AB1C24] hover:bg-[#8A151C] disabled:bg-stone-300 text-white px-3 py-1.5 rounded text-sm font-semibold"
                data-testid="announcement-publish"
              >
                {posting
                  ? <Spinner size={13} className="animate-spin" />
                  : <PaperPlaneTilt size={13} weight="fill" />}
                {isDe ? 'Veröffentlichen' : 'Publicar'}
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-xs text-[#A8A29E]">{isDe ? 'Lade…' : 'Cargando…'}</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-[#A8A29E] italic">
            {isDe
              ? 'Noch keine Mitteilungen.'
              : 'Aún no hay avisos.'}
          </p>
        ) : (
          <ul className="space-y-2">
            {items.map(a => (
              <li
                key={a.announcement_id}
                className={`rounded-lg p-3 border ${a.kind === 'auto'
                  ? 'bg-amber-50/70 border-amber-200'
                  : 'bg-stone-50 border-stone-200'}`}
                data-testid={`announcement-${a.announcement_id}`}
              >
                <div className="flex items-start gap-2">
                  {a.kind === 'auto' && (
                    <Sparkle size={14} weight="fill" className="text-amber-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#292524] whitespace-pre-wrap break-words">{a.body}</p>
                    <p className="text-[11px] text-[#A8A29E] mt-1">
                      {a.author_name || 'Habla'} · {timeAgo(a.created_at, isDe)}
                      {a.kind === 'auto' && <span className="ml-1 text-amber-700 font-semibold">· {isDe ? 'automatisch' : 'auto'}</span>}
                    </p>
                  </div>
                  {isTeacher && (
                    <button
                      onClick={() => remove(a.announcement_id)}
                      className="text-stone-400 hover:text-red-600 p-1 -m-1 flex-shrink-0"
                      title={isDe ? 'Löschen' : 'Borrar'}
                      data-testid={`announcement-delete-${a.announcement_id}`}
                    >
                      <Trash size={14} />
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
